import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Menu, X, LogOut, Search, Crown, Shield, Scissors, BarChart3, Package, FolderOpen, Gavel, MessageCircle, Fingerprint, ShoppingCart, Wallet, Trophy, Radio, FileText, ShoppingBag } from 'lucide-react';

const SHIELD_LOGO = 'https://customer-assets.emergentagent.com/job_creator-hub-699/artifacts/znp5fjmw_image_7fc4ee3c-94ac-47f2-acda-a583cc0a2f45.png';

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  const isActive = (path) => location.pathname === path;

  const navLinks = [
    { path: '/scan', label: 'Scan', icon: Search },
    { path: '/vault', label: 'Vault', icon: FolderOpen },
    { path: '/enforcement', label: 'Enforce', icon: Gavel },
    { path: '/vig', label: '.VIG', icon: Fingerprint },
    { path: '/marketplace', label: 'Market', icon: ShoppingCart },
    { path: '/treasury', label: 'Treasury', icon: Wallet },
    { path: '/streaming', label: 'Live', icon: Radio },
    { path: '/achievements', label: 'Rank', icon: Trophy },
    { path: '/editor', label: 'Studio', icon: Scissors },
    { path: '/reports', label: 'Reports', icon: FileText },
    { path: '/devkit', label: 'Dev Kit', icon: Package },
    { path: '/shop', label: 'Shop', icon: ShoppingBag },
    { path: '/subscribe', label: 'Plans', icon: Crown },
  ];

  const handleLogout = async () => {
    await logout();
    navigate('/');
    setMenuOpen(false);
  };

  return (
    <nav className="h-16 border-b border-[--ch-border] bg-[--ch-bg-card] sticky top-0 z-50 glass" data-testid="navbar">
      <div className="max-w-7xl mx-auto h-full flex items-center justify-between px-4">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2.5 group" data-testid="nav-logo">
          <img src={SHIELD_LOGO} alt="Creator Hub" className="w-8 h-8 object-contain group-hover:drop-shadow-[0_0_8px_rgba(0,240,255,0.5)] transition-all" />
          <span className="font-brand text-sm font-black hidden sm:block">
            <span className="text-[--ch-cyan]">SMITHERS</span>{' '}
            <span className="text-[--ch-magenta]">XI</span>
          </span>
        </Link>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-1">
          {navLinks.map(link => {
            const Icon = link.icon;
            return (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all ${
                  isActive(link.path)
                    ? 'text-[--ch-cyan] bg-[rgba(0,240,255,0.1)]'
                    : 'text-[--ch-text-secondary] hover:text-white'
                }`}
              >
                <Icon size={14} />
                {link.label}
              </Link>
            );
          })}
        </div>

        {/* Right side */}
        <div className="flex items-center gap-2">
          {user ? (
            <>
              <div className="flex items-center gap-2 ml-2">
                <div className="w-7 h-7 rounded-full bg-[--ch-bg-secondary] border border-[--ch-border] flex items-center justify-center text-xs font-bold text-[--ch-cyan] overflow-hidden" data-testid="nav-avatar">
                  {user.avatar_url ? (
                    <img src={user.avatar_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    user.username?.[0]?.toUpperCase() || 'U'
                  )}
                </div>
                <span className="text-xs font-semibold hidden sm:block">{user.username}</span>
                {user.subscription_tier && (
                  <span className="text-[10px] bg-[rgba(0,240,255,0.1)] text-[--ch-cyan] px-1.5 py-0.5 rounded font-bold hidden sm:block">
                    {user.subscription_tier.toUpperCase()}
                  </span>
                )}
                <button
                  data-testid="nav-logout-btn"
                  onClick={handleLogout}
                  className="text-[--ch-text-secondary] hover:text-[--ch-error] transition-colors ml-1"
                  title="Logout"
                >
                  <LogOut size={16} />
                </button>
              </div>
            </>
          ) : (
            <button
              data-testid="nav-signin-btn"
              onClick={() => navigate('/auth')}
              className="btn-neon px-4 py-1.5 rounded-lg text-xs"
            >
              Sign In
            </button>
          )}

          {/* Mobile menu toggle */}
          <button
            data-testid="nav-mobile-menu"
            onClick={() => setMenuOpen(!menuOpen)}
            className="md:hidden text-[--ch-text-secondary] hover:text-white ml-2"
          >
            {menuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="md:hidden absolute top-16 left-0 right-0 bg-[--ch-bg-card] border-b border-[--ch-border] z-40 animate-fade-in-up" data-testid="mobile-menu">
          <div className="p-4 space-y-1">
            {navLinks.map(link => {
              const Icon = link.icon;
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setMenuOpen(false)}
                  data-testid={`mobile-nav-${link.label.toLowerCase().replace(' ', '-')}`}
                  className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm transition-all ${
                    isActive(link.path) ? 'text-[--ch-cyan] bg-[rgba(0,240,255,0.05)]' : 'text-[--ch-text-secondary]'
                  }`}
                >
                  <Icon size={16} /> {link.label}
                </Link>
              );
            })}
            <Link to="/dmca" onClick={() => setMenuOpen(false)}
              className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm text-[--ch-text-secondary]" data-testid="mobile-nav-legal">
              <Shield size={16} /> Legal
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}
